import yaml from 'js-yaml'

/**
 * Extracts the latest version for each minor version from the given config maps.
 */
export const extractSupportedK8sVersions = (configMaps: any[]): string[] => {
  if (!Array.isArray(configMaps) || configMaps.length === 0) return []

  const allVersions = new Set<string>()

  for (const configMap of configMaps) {
    // Check if the config map is a cluster version config map
    const name = configMap.metadata?.name || ''
    if (!name.includes('cluster-version')) continue

    // Check if the config map has supportedVersion label and it is set to true
    const labels = configMap.metadata?.labels
    if (
      !Object.keys(labels).includes('kaapi.pf9.io/supportedVersion') ||
      !configMap.metadata?.labels['kaapi.pf9.io/supportedVersion']
    )
      continue

    const data = configMap.data || {}
    if (!data.supportedVersions) continue

    let versions: string[]
    try {
      // Parse supportedVersions into an array, ensuring it handles multiple versions correctly
      versions = data.supportedVersions
        .split('\n')
        .map((version) => version.replace(/^-\s*/, '').trim()) // Remove the "- " part
        .filter(Boolean)
    } catch (error) {
      console.error('Error parsing supported versions in config map:', error)
      continue
    }

    // Add all versions to the Set to avoid duplicates
    versions.forEach((version) => {
      allVersions.add(version)
    })
  }

  // Convert the Set back to an array, sort it and return in descending order
  return Array.from(allVersions)
    .sort(compareVersions)
    .reverse() // Sort in descending order
}
/**
 * Compares two semantic versions.
 * Returns:
 * - 1 if versionA > versionB
 * - -1 if versionA < versionB
 * - 0 if equal
 */
// const compareVersions = (versionA, versionB) => {
//   const [majorA, minorA, patchA] = versionA.split('.').map(Number)
//   const [majorB, minorB, patchB] = versionB.split('.').map(Number)

//   if (majorA !== majorB) return majorA - majorB
//   if (minorA !== minorB) return minorA - minorB
//   return patchA - patchB
// }
const compareVersions = (versionA: string, versionB: string): number => {
  const [majorA, minorA, patchA] = versionA.split('.').map(Number)
  const [majorB, minorB, patchB] = versionB.split('.').map(Number)

  if (majorA !== majorB) return majorA - majorB
  if (minorA !== minorB) return minorA - minorB
  return patchA - patchB
}

/**
 * @summary  Return MIG profiles from config maps - options (for dropdown) and profiles (for form)
 * @param configMaps
 * @param modelName
 * @param memory
 * @returns
 */
export const getGPUProfiles = (configMaps: any[], modelName: string, memory: string) => {
  const memoryInGB = Number(memory) ? Math.ceil(Number(memory) / 1024) : 0
  const modelNameLowerCase = modelName?.toLowerCase()
  // If the GPU config map has gpu-memory label, then check for matching memory label
  // Or else check for matching gpu model label
  const matchingGPUConfigMap = configMaps.find((configMap) =>
    configMap?.metadata?.labels?.['gpu-memory']
      ? modelNameLowerCase?.includes(configMap?.metadata?.labels?.['gpu-model']?.toLowerCase()) &&
        configMap?.metadata?.labels?.['gpu-memory']?.toString() === memoryInGB?.toString()
      : modelNameLowerCase?.includes(configMap?.metadata?.labels?.['gpu-model']?.toLowerCase()),
  )

  // If no matching GPU config map is found, return empty options
  if (!matchingGPUConfigMap) {
    return { gpuProfileOptions: [], gpuSizes: {} }
  }

  const jsonFormattedGPUProfiles = yaml.load(matchingGPUConfigMap?.data?.['config.yaml'])

  // Sizes for form
  const gpuSizes = Object.keys(jsonFormattedGPUProfiles).reduce((accm, profile) => {
    return {
      ...accm,
      [profile]: Object.keys(jsonFormattedGPUProfiles[profile])
        .slice(1)
        .reduce((accm2, key) => {
          return {
            ...accm2,
            [key]: jsonFormattedGPUProfiles[profile][key],
          }
        }, {}),
    }
  }, {})

  // Options for dropdown
  const gpuProfileOptions = Object.keys(jsonFormattedGPUProfiles).map((profile) => {
    return {
      label: `${jsonFormattedGPUProfiles[
        profile
      ].displayname.toUpperCase()} (${formatGPUSizesToString(gpuSizes[profile])})`,
      value: profile,
    }
  })

  return { gpuProfileOptions, gpuSizes }
}

/**
 * @summary  Return GPU sizes as string
 * @param gpuSizes
 * @param separator
 * @param multipleSeparator
 * @returns
 */
export const formatGPUSizesToString = (
  gpuSizes: Record<string, string>,
  separator: string = ', ',
  multipleSeparator: string = 'x',
) => {
  return Object.keys(gpuSizes)?.reduce((accm, key) => {
    const newAccm = accm ? accm + separator : ''
    return `${newAccm}${key} ${multipleSeparator} ${gpuSizes[key]}`
  }, '')
}

// Get DU Internal IP
export const getDUInternalIP = (configMaps: any[]) => {
  if (!Array.isArray(configMaps) || configMaps.length === 0) return ''

  const duConfigMap = configMaps.find((cm) => cm.metadata.name === 'du-config')
  return duConfigMap?.data?.['du-vip']
}

// Filter flavors based on source type
export const filterFlavorsBasedOnSourceType = (vmFlavors: any[], sourceType: string) =>
  vmFlavors?.filter((flavor) => {
    if (flavor.ram >= 4096 && flavor.vcpus >= 2) {
      if (sourceType === 'image') {
        return flavor.disk > 0
      }
      if (sourceType === 'volume') {
        return flavor.disk === 0
      }
    }
    return false
  })

export const DEFAULT_VOLUME_SIZE = 20
